import { useCallback, useMemo } from "react";

import { MagnifyingGlassIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";

import Beacon from "@gx/dls/beacons/Beacon";
import Button, { ButtonVariant } from "@gx/dls/buttons/Button";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, { AlignItems } from "@gx/dls/containers/FlexWrapper";
import MultiSelectInput, { PinnedOptions } from "@gx/dls/inputs/MultiSelectInput";
import { SelectInputOption } from "@gx/dls/inputs/SelectInput";
import TextInput from "@gx/dls/inputs/TextInput";
import Text, { TextVariant, TextWeight } from "@gx/dls/text/Text";
import { SourceStatus, SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import BaseToolbar from "@/layouts/components/BaseToolbar";

import { CreateProjectFormActionType } from "@/pages/projects/forms/create/actions";
import { useCreateProjectFormContext } from "@/pages/projects/forms/create/hooks";
import { ALL_SOURCES } from "@/pages/sources/components/create/constants";
import SourceIcon, { SourceIconSize } from "@/pages/sources/components/SourceIcon";
import {
  FILTERABLE_SOURCE_STATUSES,
  SOURCE_STATUS_TO_BEACON_VARIANT_MAP,
  SOURCE_STATUS_TO_TEXT_MAP,
  SOURCE_STATUS_TO_TEXT_VARIANT_MAP,
  SOURCE_TYPE_TO_LABEL_MAP,
} from "@/pages/sources/constants";

const FILTERABLE_SOURCE_TYPES = ALL_SOURCES.filter((s) => s !== SourceType.UNSPECIFIED);

const SelectProjectSourcesToolbar = () => {
  const { state, dispatch } = useCreateProjectFormContext();

  const navigate = useNavigate();

  // Source type options with icons
  const sourceTypeOptions: SelectInputOption[] = useMemo(
    () =>
      FILTERABLE_SOURCE_TYPES.map((sourceType) => ({
        id: sourceType.toString(),
        label: SOURCE_TYPE_TO_LABEL_MAP[sourceType],
        value: sourceType,
        icon: <SourceIcon sourceType={sourceType} size={SourceIconSize.XSMALL} />,
      })),
    [],
  );

  // Source type pinned options
  const sourceTypePinnedOptions: PinnedOptions[] = useMemo(
    () => [
      {
        id: "all-source-types",
        label: "All source types",
        optionIds: FILTERABLE_SOURCE_TYPES.map((sourceType) => sourceType.toString()),
      },
    ],
    [],
  );

  // Selected source type options
  const selectedSourceTypeOptions = useMemo(() => {
    const selectedIds = new Set(
      state.selectedSourceTypes.map((sourceType) => sourceType.toString()),
    );
    return sourceTypeOptions.filter((option) => selectedIds.has(option.id));
  }, [state.selectedSourceTypes, sourceTypeOptions]);

  const statusOptions: SelectInputOption[] = useMemo(
    () =>
      FILTERABLE_SOURCE_STATUSES.map((status) => ({
        id: status.toString(),
        label: SOURCE_STATUS_TO_TEXT_MAP[status],
        value: status,
        icon: <Beacon variant={SOURCE_STATUS_TO_BEACON_VARIANT_MAP[status]} />,
      })),
    [],
  );

  const statusPinnedOptions: PinnedOptions[] = useMemo(
    () => [
      {
        id: "all-statuses",
        label: "All statuses",
        optionIds: FILTERABLE_SOURCE_STATUSES.map((sourceStatus) => sourceStatus.toString()),
      },
    ],
    [],
  );

  // Source status options with beacon icons
  const sourceStatusOptions: SelectInputOption[] = useMemo(
    () =>
      FILTERABLE_SOURCE_STATUSES.map((status) => ({
        id: status.toString(),
        label: SOURCE_STATUS_TO_TEXT_MAP[status],
        value: status,
        icon: <Beacon variant={SOURCE_STATUS_TO_BEACON_VARIANT_MAP[status]} />,
        variant: SOURCE_STATUS_TO_TEXT_VARIANT_MAP[status],
      })),
    [],
  );

  const selectedStatusOptions = useMemo(() => {
    const selectedIds = new Set(
      state.selectedStatuses.map((sourceStatus) => sourceStatus.toString()),
    );
    return statusOptions.filter((option) => selectedIds.has(option.id));
  }, [state.selectedStatuses, statusOptions]);

  const handleSelectedSourceTypesChange = useCallback(
    (selectedOptions: SelectInputOption[]) => {
      dispatch({
        type: CreateProjectFormActionType.SET_SELECTED_SOURCE_TYPES,
        payload: selectedOptions.map((option) => Number(option.id) as SourceType),
      });
    },
    [dispatch],
  );

  const handleResetSourceTypes = useCallback(() => {
    dispatch({
      type: CreateProjectFormActionType.SET_SELECTED_SOURCE_TYPES,
      payload: FILTERABLE_SOURCE_TYPES,
    });
  }, [dispatch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      dispatch({
        type: CreateProjectFormActionType.SET_SEARCH,
        payload: value,
      });
    },
    [dispatch],
  );

  const handleSelectedStatusesChange = useCallback(
    (selectedOptions: SelectInputOption[]) => {
      dispatch({
        type: CreateProjectFormActionType.SET_SELECTED_STATUSES,
        payload: selectedOptions.map((option) => Number(option.id) as SourceStatus),
      });
    },
    [dispatch],
  );

  const handleResetStatuses = useCallback(() => {
    dispatch({
      type: CreateProjectFormActionType.SET_SELECTED_STATUSES,
      payload: FILTERABLE_SOURCE_STATUSES,
    });
  }, [dispatch]);

  const handleSearchClear = useCallback(() => {
    dispatch({
      type: CreateProjectFormActionType.SET_SEARCH,
      payload: "",
    });
  }, [dispatch]);

  const handleNewSourceClick = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, createSource: true }),
    });
  }, [navigate]);

  return (
    <BaseToolbar
      leadingActions={[
        <TextInput
          key="search"
          placeholder="Search"
          leading={{ icon: MagnifyingGlassIcon }}
          trailing={state.search ? { icon: XIcon, onClick: handleSearchClear } : undefined}
          onChange={handleSearchChange}
          value={state.search}
          isDisabled={false}
          width={240}
        />,
        <MultiSelectInput
          key="type"
          placeholder="Source type"
          renderSelectedContent={(selectedOptions) => {
            if (selectedOptions.length === FILTERABLE_SOURCE_TYPES.length) {
              return (
                <Text weight={TextWeight.MEDIUM} isEllipsis>
                  All source types
                </Text>
              );
            }
            if (selectedOptions.length === 0) {
              return (
                <Text variant={TextVariant.PRIMARY} weight={TextWeight.MEDIUM} isEllipsis>
                  No source types
                </Text>
              );
            }
            if (selectedOptions.length === 1) {
              const sourceType = selectedOptions[0].value as SourceType;
              return (
                <FlexWrapper alignItems={AlignItems.CENTER} gap={6}>
                  <FlexItem shrink={0}>
                    <SourceIcon sourceType={sourceType} size={SourceIconSize.XSMALL} />
                  </FlexItem>
                  <Text variant={TextVariant.PRIMARY} weight={TextWeight.MEDIUM} isEllipsis>
                    {selectedOptions[0].label}
                  </Text>
                </FlexWrapper>
              );
            }
            return (
              <Text variant={TextVariant.PRIMARY} isEllipsis>
                {selectedOptions.length} source types
              </Text>
            );
          }}
          options={sourceTypeOptions}
          value={selectedSourceTypeOptions}
          onChange={handleSelectedSourceTypesChange}
          onReset={handleResetSourceTypes}
          pinnedOptions={sourceTypePinnedOptions}
          width={160}
          dropdownWidth={240}
        />,
        <MultiSelectInput
          key="status"
          placeholder="Select statuses"
          renderSelectedContent={(selectedOptions) => {
            if (selectedOptions.length === FILTERABLE_SOURCE_STATUSES.length) {
              return <Text isEllipsis>All statuses</Text>;
            }
            if (selectedOptions.length === 0) {
              return (
                <Text variant={TextVariant.PRIMARY} isEllipsis>
                  No statuses
                </Text>
              );
            }
            if (selectedOptions.length === 1) {
              const status = selectedOptions[0].value as SourceStatus;
              return (
                <FlexWrapper alignItems={AlignItems.CENTER} gap={6}>
                  <FlexItem shrink={0}>
                    <Beacon variant={SOURCE_STATUS_TO_BEACON_VARIANT_MAP[status]} />
                  </FlexItem>
                  <Text variant={TextVariant.PRIMARY} isEllipsis>
                    {selectedOptions[0].label}
                  </Text>
                </FlexWrapper>
              );
            }
            return (
              <Text variant={TextVariant.PRIMARY} isEllipsis>
                {selectedOptions.length} statuses
              </Text>
            );
          }}
          options={sourceStatusOptions}
          value={selectedStatusOptions}
          onChange={handleSelectedStatusesChange}
          onReset={handleResetStatuses}
          pinnedOptions={statusPinnedOptions}
          width={120}
          dropdownWidth={180}
        />,
      ]}
      trailingActions={[
        <Button
          key="new-source"
          label="New source"
          variant={ButtonVariant.TERTIARY}
          icon={PlusIcon}
          onClick={handleNewSourceClick}
        />,
      ]}
    />
  );
};

export default SelectProjectSourcesToolbar;
